import { useContext, useState, useEffect } from 'react';
import { CartContext } from '../context/CartContext';
import { kotService } from '../services/kotService';
import { toast } from 'react-hot-toast';

/**
 * Map API order status to UI status
 * API statuses: SENT, PREPARING, READY, SERVED, COMPLETED, CANCELLED, etc.
 * UI statuses: sent, preparing, ready, served, cancelled
 */
const mapOrderStatus = (apiStatus) => {
  if (!apiStatus) return 'sent';

  const status = apiStatus.toLowerCase().trim();

  // Direct mappings
  if (status === 'cancelled') return 'cancelled';
  if (status === 'ready') return 'ready';
  if (status === 'served' || status === 'completed') return 'served';
  if (status === 'preparing' || status === 'in_progress' || status === 'in progress') return 'preparing';
  if (status === 'sent' || status === 'pending' || status === 'new') return 'sent';

  // Fallback: return as-is (lowercase) if it matches UI status, otherwise default to 'sent'
  const validStatuses = ['sent', 'preparing', 'ready', 'served', 'cancelled'];
  return validStatuses.includes(status) ? status : 'sent';
};

/**
 * useKOT Hook
 * Custom hook for KOT operations with backend + local state sync
 * @param {number|string} tableId - Table ID to fetch active orders for
 * @param {string} tableSplit - Table split name (e.g., 'A', 'B', 'C')
 * @param {Object} options - Optional configuration
 * @param {boolean} options.enabled - Whether to enable automatic fetching (default: true)
 * @param {boolean} options.skipIfNew - Skip fetch if no items sent to kitchen yet (default: true)
 */
export const useKOT = (tableId = null, tableSplit = null, options = {}) => {
  const { sendKOT: sendKOTLocal, cancelKOT: cancelKOTLocal, getCurrentOrder, getCurrentCartItems, ordersRefreshCounter, triggerOrdersRefresh } = useContext(CartContext);

  const { enabled = true, skipIfNew = true } = options;

  // State for active orders
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Send KOT to kitchen (backend + local update)
   * @param {Object} orderData - Order data to send to backend
   * @returns {Promise<Object>} - Created KOT response
   */
  const sendKOT = async (orderData) => {
    try {
      // ✅ Step 1: Send to backend
      const result = await kotService.sendKOT(orderData);

      // ✅ Step 2: Update local state for instant UI feedback
      sendKOTLocal(result?.data || orderData);

      // ✅ Step 3: Refresh active orders from API
      await fetchActiveOrders();

      // ✅ Step 4: Notify user
      toast.success('KOT sent to kitchen!', {
        icon: '👨‍🍳',
        duration: 2000,
      });

      return result?.data;
    } catch (error) {
      console.error('❌ Send KOT error:', error);
      toast.error(error?.response?.data?.message || 'Failed to send KOT');
      throw error;
    }
  };

  /**
   * Cancel KOT
   * @param {string|number} kotId - KOT ID to cancel
   * @param {string} reason - Cancellation reason
   * @returns {Promise<boolean>}
   */
  const cancelKOT = async (kotId, reason) => {
    try {
      // ✅ Step 1: Cancel from backend
      await kotService.cancelKOT(kotId, reason);

      // ✅ Step 2: Trigger refresh for ALL useKOT instances (including BillSummary)
      if (typeof triggerOrdersRefresh === 'function') {
        triggerOrdersRefresh();
      }

      // ✅ Step 3: Refresh THIS instance's active orders from API
      await fetchActiveOrders();

      // Note: We don't call cancelKOTLocal because once a KOT is sent to kitchen,
      // it's stored on backend, not in CartContext's local state.
      // The backend API already marked it as cancelled and isActive=false.

      toast.success('KOT cancelled successfully', { icon: '🛑' });
      return true;
    } catch (error) {
      console.error('❌ Cancel KOT error:', error);
      toast.error(error?.response?.data?.message || 'Failed to cancel KOT');
      throw error;
    }
  };

  /**
   * Update KOT status (e.g. SENT → IN_PROGRESS → COMPLETED)
   * @param {string|number} kotId - KOT ID
   * @param {string} status - New status value
   * @returns {Promise<boolean>}
   */
  const updateKOTStatus = async (kotId, status) => {
    try {
      // ✅ Step 1: Update status on backend
      await kotService.updateKOTStatus(kotId, status);

      // ✅ Step 2: Notify
      toast.success(`KOT status updated to "${status}"`, { icon: '✅' });
      return true;
    } catch (error) {
      console.error('❌ Update KOT status error:', error);
      toast.error(error?.response?.data?.message || 'Failed to update KOT status');
      throw error;
    }
  };

  /**
   * Fetch active orders for a table split from API
   * @returns {Promise<void>}
   */
  const fetchActiveOrders = async () => {
    // Only fetch if enabled and both tableId and tableSplit are provided
    if (!enabled || !tableId || !tableSplit) {
      setActiveOrders([]);
      setLoading(false);
      return;
    }

    // ✅ Skip API call for brand new orders (optimization)
    if (skipIfNew) {
      const currentOrder = getCurrentOrder();
      const cartItems = getCurrentCartItems();

      // Only skip if this is a TRULY new order (no items in cart AND no KOTs exist)
      const isBrandNewOrder = (!cartItems || cartItems.length === 0) && (!currentOrder?.kots || currentOrder.kots.length === 0);

      if (isBrandNewOrder) {
        setActiveOrders([]);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const response = await kotService.getActiveOrders(tableId, tableSplit);

      // Handle different response structures
      const ordersData = response?.data?.data || response?.data || response || [];

      // Transform API response to match KOT structure
      const transformedOrders = Array.isArray(ordersData)
        ? ordersData.map((order) => {
            // Parse cancellation info from notes if status is CANCELLED
            let cancellationReason = null;
            let cancelledAt = null;

            if (order.orderStatus === 'CANCELLED' && order.notes) {
              const cancelMatch = order.notes.match(/CANCELLED: (.*?) \| Cancelled at: (.*)/);
              if (cancelMatch) {
                cancellationReason = cancelMatch[1];
                cancelledAt = cancelMatch[2];
              }
            }

            return {
              id: order.id,
              kotNumber: order.orderNumber || order.id, // Use order number or ID as KOT number
              tableId: order.tableId,
              tableSplit: order.tableSplit,
              items: (order.items || []).map(item => ({
                id: item.id,
                menuItemId: item.menuItemId,
                variantId: item.variantId,
                name: item.itemName,
                itemName: item.itemName,
                variantName: item.variantName,
                quantity: item.quantity,
                price: item.unitPrice,
                unitPrice: item.unitPrice,
                finalPrice: item.unitPrice,
                station: item.station || 'kitchen',
                kitchenStationId: item.kitchenStationId || null,
                kitchenStationName: item.kitchenStationName || null,
                itemStatus: mapOrderStatus(item.itemStatus || item.status), // Map item status
                customization: item.specialInstructions ? {
                  customInstructions: item.specialInstructions
                } : null,
              })),
              totalAmount: order.totalAmount || 0,
              status: mapOrderStatus(order.orderStatus), // Map API status to UI status
              timestamp: order.createdDateTime || new Date().toISOString(),
              createdAt: order.createdDateTime || new Date().toISOString(),
              notes: order.notes || null,
              cancellationReason: cancellationReason,
              cancelledAt: cancelledAt,
            };
          })
        : [];

      setActiveOrders(transformedOrders);
      setLoading(false);
    } catch (err) {
      console.error('❌ Error fetching active orders:', err);
      setError(err.message || 'Failed to load active orders');
      setActiveOrders([]);
      setLoading(false);
    }
  };

  /**
   * Auto-fetch active orders when tableId, tableSplit, KOTs change, or refresh is triggered
   */
  useEffect(() => {
    fetchActiveOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, tableSplit, getCurrentOrder()?.kots?.length, ordersRefreshCounter]);

  return {
    sendKOT,
    cancelKOT,
    updateKOTStatus,
    activeOrders,
    loading,
    error,
    refreshActiveOrders: fetchActiveOrders,
  };
};

export default useKOT;
